import { ArrowRight, Check, Copy } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { VerificationService } from "@/modules/verification/services/verificationService";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaCombobox, type ColocaComboboxOption } from "@/shared/components/forms/ColocaCombobox";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaStepper, type ColocaStepperStep } from "@/shared/components/navigation/ColocaStepper";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { PARENT_CLIENT_LIST_LIMIT } from "../constants/clients.constants";
import { ClientsService } from "../services/clientsService";
import type {
  ClientAccount,
  CreateClientBody,
  CreateOrganizationBody,
  CreateOrganizationResponse,
  OrganizationTreeItem,
} from "../types/clients-api.types";
import { canWriteClientAccounts } from "../utils/clientsPermissions";
import { displayClientName } from "../utils/clientsTableHelpers";
import { rememberIssuedKycLink } from "../utils/issuedKycLinkCache";
import { displayNameFromCreateBody } from "../utils/normalizeClient";
import {
  BUSINESS_WIZARD_STEP_COUNT,
  BusinessClientForm,
  type BusinessWizardHandle,
} from "./create/BusinessClientForm";
import { ClientSuccessStep, type CreatedClientSummary } from "./create/ClientSuccessStep";
import { ClientTypeChoice, type ClientTypeKind } from "./create/ClientTypeChoice";
import { CreateEntityChoice, type CreateEntityKind } from "./create/CreateEntityChoice";
import {
  ORGANIZATION_WIZARD_STEP_COUNT,
  OrganizationClientForm,
  type OrganizationWizardHandle,
} from "./create/OrganizationClientForm";
import { type IssuedKycLink, OrganizationSuccessStep } from "./create/OrganizationSuccessStep";
import { PersonClientForm } from "./create/PersonClientForm";

const PERSON_FORM_ID = "create-client-person-form";
const BUSINESS_FORM_ID = "create-client-business-form";
const ORGANIZATION_FORM_ID = "create-client-organization-form";
const PARENT_ORGANIZATION_SELECT_ID = "create-client-parent-organization";

/**
 * Unified create flow rendered as a side drawer (mock wizard container — same `ColocaDrawer` pattern as the
 * RBAC role/policy editors in settings). Screens mirror the mock: internal users pick the entity first
 * (`choice`), the client branch selects the type through cards (`client_type`, footer Continue) and then
 * shows the matching form (`client_form`); org members start directly at `client_type` (the backend scopes
 * `organization_id` from their JWT).
 */
type CreateScreen = "choice" | "organization" | "client_type" | "client_form";

export interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Restrict the wizard to the client branch — skips the internal entity choice (dispersions shortcut). */
  clientOnly?: boolean;
  /**
   * Dispersions shortcut: reports the created client so the caller can select it immediately. The drawer
   * always stays open on the success step (engine KYC link + optional account form), with or without this.
   */
  onClientCreated?: (client: CreatedClientSummary) => void;
  /** Offers registering an account on the success step, locked to this currency (destination currency). */
  successAccountCurrency?: string;
  /**
   * Seeds that account form with a destination the caller already holds — the dispersions "save this
   * recipient" action, which runs after the payout went out and must not ask for the key it just paid.
   */
  successAccountPrefill?: { bank: string; methodFields: Record<string, string> };
  /** Called with the created account right before the drawer auto-closes. */
  onAccountCreated?: (client: CreatedClientSummary, account: ClientAccount) => void;
}

function CreateClientModal({
  isOpen,
  onClose,
  onSuccess,
  clientOnly = false,
  onClientCreated,
  successAccountCurrency,
  successAccountPrefill,
  onAccountCreated,
}: CreateClientModalProps) {
  const { t } = useTranslations("clients");
  const { isInternalUser, user } = useAuth();
  const initialScreen: CreateScreen = isInternalUser && !clientOnly ? "choice" : "client_type";
  const [screen, setScreen] = useState<CreateScreen>(initialScreen);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientFormType, setClientFormType] = useState<ClientTypeKind | null>(null);
  const [businessStep, setBusinessStep] = useState(0);
  const [organizationStep, setOrganizationStep] = useState(0);
  const [createdOrganization, setCreatedOrganization] = useState<CreateOrganizationResponse | null>(null);
  const [createdOrgKycLink, setCreatedOrgKycLink] = useState<IssuedKycLink | null>(null);
  const [createdClient, setCreatedClient] = useState<CreatedClientSummary | null>(null);
  const [createdClientKycLink, setCreatedClientKycLink] = useState<IssuedKycLink | null>(null);
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [successLinkCopied, setSuccessLinkCopied] = useState(false);
  const [parentClientId, setParentClientId] = useState<string | null>(null);
  const [parentClientOptions, setParentClientOptions] = useState<ColocaComboboxOption[]>([]);
  const [parentClientsLoading, setParentClientsLoading] = useState(false);
  const [parentClientError, setParentClientError] = useState<string | null>(null);
  const businessWizardRef = useRef<BusinessWizardHandle>(null);
  const organizationWizardRef = useRef<OrganizationWizardHandle>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    },
    [],
  );

  const activeFormId =
    screen === "organization"
      ? ORGANIZATION_FORM_ID
      : clientFormType === "business"
        ? BUSINESS_FORM_ID
        : PERSON_FORM_ID;
  const isBusinessIntermediateStep =
    screen === "client_form" &&
    clientFormType === "business" &&
    businessStep < BUSINESS_WIZARD_STEP_COUNT - 1;
  const isOrganizationIntermediateStep =
    screen === "organization" && organizationStep < ORGANIZATION_WIZARD_STEP_COUNT - 1;
  // Internal callers must pick the parent organization before creating a client.
  const clientSubmitBlocked = screen === "client_form" && isInternalUser && !parentClientId;

  const fetchParentClients = useCallback(async () => {
    setParentClientsLoading(true);
    setParentClientError(null);
    try {
      const { data } = await ClientsService.fetchTree({ skip: 0, limit: PARENT_CLIENT_LIST_LIMIT });
      const options: ColocaComboboxOption[] = data.data.map((item: OrganizationTreeItem) => ({
        value: item.id,
        label: displayClientName(item),
      }));
      setParentClientOptions(options);
    } catch (err) {
      setParentClientError(HttpApiService.getErrorMessage(err));
      setParentClientOptions([]);
    } finally {
      setParentClientsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && isInternalUser) fetchParentClients();
  }, [isOpen, isInternalUser, fetchParentClients]);

  const resetForm = useCallback(() => {
    setScreen(initialScreen);
    setClientFormType(null);
    setBusinessStep(0);
    setOrganizationStep(0);
    setCreatedOrganization(null);
    setCreatedOrgKycLink(null);
    setCreatedClient(null);
    setCreatedClientKycLink(null);
    setAccountSubmitting(false);
    setSuccessLinkCopied(false);
    setParentClientId(null);
    setParentClientError(null);
  }, [initialScreen]);

  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen, resetForm]);

  const handlePickEntity = useCallback((entity: CreateEntityKind) => {
    setScreen(entity === "organization" ? "organization" : "client_type");
  }, []);

  const handleSubmitOrganization = useCallback(
    async (body: CreateOrganizationBody) => {
      setIsSubmitting(true);
      try {
        const { data } = await ClientsService.createOrganization(body);
        colocaToast.success(t("toast_create_organization_success"));
        // Issue the corporate KYC link through the verification engine so the success screen can share it
        // (mock `org_success`). Best-effort: the screen falls back to the pending note when it fails.
        let kycLink: IssuedKycLink | null = null;
        if (data.organization_identity_id) {
          try {
            const { data: link } = await VerificationService.issueLink(data.organization_identity_id);
            kycLink = { url: link.verification_url, expiresAt: link.expires_at ?? null };
            // Remember it so the detail KYC section reuses this link instead of re-issuing (which
            // would expire it on the backend).
            rememberIssuedKycLink(data.organization_identity_id, kycLink);
          } catch {
            // keep kycLink null
          }
        }
        // Keep the drawer open on the success screen; the list refreshes behind it.
        setCreatedOrganization(data);
        setCreatedOrgKycLink(kycLink);
        onSuccess?.();
      } catch (err) {
        colocaToast.error(HttpApiService.getErrorMessage(err));
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSuccess, t],
  );

  const handleCreateClient = useCallback(
    async (body: CreateClientBody) => {
      // Implicit form submission (Enter) bypasses the disabled footer button — re-check the parent-org
      // requirement here so internal users can never post a client without an organization.
      if (isInternalUser && !parentClientId) return;
      setIsSubmitting(true);
      try {
        const { data } = await ClientsService.create(body);
        colocaToast.success(t("toast_create_client_success"));
        // Every entry point (clients page and dispersions shortcut) stays open on the success step so
        // the engine verification link is visible right after create. The create response has no
        // identity joined, so the display name comes from the submitted body (email/id as fallback).
        const summary: CreatedClientSummary = {
          id: data.id,
          identityId: data.identity_id ?? null,
          displayName: displayNameFromCreateBody(body) || data.email || data.id,
        };
        // Issue the KYC link through the verification engine — same best-effort as the organization
        // flow: the success step falls back to the pending note when it fails.
        let kycLink: IssuedKycLink | null = null;
        if (summary.identityId) {
          try {
            const { data: link } = await VerificationService.issueLink(summary.identityId);
            kycLink = { url: link.verification_url, expiresAt: link.expires_at ?? null };
            // Remember it so the detail KYC section reuses this link instead of re-issuing (which
            // would expire it on the backend).
            rememberIssuedKycLink(summary.identityId, kycLink);
          } catch {
            // keep kycLink null
          }
        }
        setCreatedClient(summary);
        setCreatedClientKycLink(kycLink);
        onClientCreated?.(summary);
        onSuccess?.();
      } catch (err) {
        if (HttpApiService.isAxiosError(err) && err.response?.status === 409) {
          colocaToast.error(t("toast_create_client_duplicate"));
        } else {
          colocaToast.error(HttpApiService.getErrorMessage(err));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSuccess, t, isInternalUser, parentClientId, onClientCreated],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isSubmitting && !accountSubmitting) onClose();
    },
    [onClose, isSubmitting, accountSubmitting],
  );

  // Success-step account form (dispersions shortcut): report the account and auto-close the drawer.
  const handleAccountCreated = useCallback(
    (account: ClientAccount) => {
      if (createdClient) onAccountCreated?.(createdClient, account);
      onClose();
    },
    [createdClient, onAccountCreated, onClose],
  );

  const handleCopySuccessLink = useCallback(() => {
    if (!createdOrgKycLink) return;
    navigator.clipboard
      .writeText(createdOrgKycLink.url)
      .then(() => {
        setSuccessLinkCopied(true);
        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = setTimeout(() => setSuccessLinkCopied(false), 2000);
      })
      .catch(() => {});
  }, [createdOrgKycLink]);

  // Back walks the sub-wizard first, then the screens: client_form → client_type → choice (internal only).
  const handleBack = useCallback(() => {
    if (screen === "organization") {
      if (organizationStep > 0) organizationWizardRef.current?.goBack();
      else setScreen("choice");
      return;
    }
    if (screen === "client_form") {
      if (clientFormType === "business" && businessStep > 0) {
        businessWizardRef.current?.goBack();
        return;
      }
      setScreen("client_type");
      return;
    }
    if (screen === "client_type" && isInternalUser && !clientOnly) setScreen("choice");
  }, [screen, organizationStep, clientFormType, businessStep, isInternalUser, clientOnly]);

  // Mock `showBack`: every screen except the entity choice — and except client_type for org members
  // (or for anyone in the client-only shortcut, which has no choice screen to go back to).
  const showBackButton =
    screen === "organization" ||
    screen === "client_form" ||
    (screen === "client_type" && isInternalUser && !clientOnly);

  // The account offer on the success step needs both the shortcut prop and the accounts write permission.
  const offersAccountStep = !!successAccountCurrency && canWriteClientAccounts(user);

  // Sticky drawer footer — same layout as the RBAC editor drawers (ml-auto action group on the right).
  const footer = createdOrganization ? (
    <div className="flex w-full items-center gap-2">
      <div className="ml-auto flex items-center gap-2">
        {createdOrgKycLink && (
          <ColocaButton
            type="button"
            variant="default"
            size="sm"
            icon={successLinkCopied ? Check : Copy}
            onClick={handleCopySuccessLink}
          >
            {successLinkCopied ? t("kyc_link_copied") : t("kyc_link_copy")}
          </ColocaButton>
        )}
        <ColocaButton type="button" variant="primary" size="sm" onClick={onClose}>
          {t("org_success_done")}
        </ColocaButton>
      </div>
    </div>
  ) : createdClient ? (
    // Client success step (dispersions shortcut): the primary action is the account form's own submit,
    // so the footer only offers skipping — or a plain "Done" when the account offer is not available.
    <div className="flex w-full items-center gap-2">
      <div className="ml-auto flex items-center gap-2">
        <ColocaButton
          type="button"
          variant={offersAccountStep ? "ghost" : "primary"}
          size="sm"
          onClick={onClose}
          disabled={accountSubmitting}
        >
          {offersAccountStep ? t("client_success_skip") : t("org_success_done")}
        </ColocaButton>
      </div>
    </div>
  ) : (
    <div className="flex w-full items-center gap-2">
      <div className="ml-auto flex items-center gap-2">
        <ColocaButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => handleOpenChange(false)}
          disabled={isSubmitting}
        >
          {t("modal_cancel")}
        </ColocaButton>
        {showBackButton && (
          <ColocaButton type="button" variant="ghost" size="sm" onClick={handleBack} disabled={isSubmitting}>
            {t("wizard_back")}
          </ColocaButton>
        )}
        {screen === "client_type" && (
          <ColocaButton
            type="button"
            variant="primary"
            size="sm"
            iconTrailing={ArrowRight}
            // Mock `canContinue = !!identityType`.
            disabled={!clientFormType}
            onClick={() => setScreen("client_form")}
          >
            {t("wizard_continue")}
          </ColocaButton>
        )}
        {(screen === "organization" || screen === "client_form") &&
          (isBusinessIntermediateStep || isOrganizationIntermediateStep ? (
            <ColocaButton
              type="button"
              variant="primary"
              size="sm"
              iconTrailing={ArrowRight}
              // The parent-organization list only gates the CLIENT branch — never the organization wizard.
              disabled={clientSubmitBlocked || (screen === "client_form" && parentClientsLoading)}
              onClick={() =>
                void (screen === "organization"
                  ? organizationWizardRef.current?.goNext()
                  : businessWizardRef.current?.goNext())
              }
            >
              {t("wizard_continue")}
            </ColocaButton>
          ) : (
            <ColocaButton
              type="submit"
              form={activeFormId}
              variant="primary"
              size="sm"
              disabled={
                isSubmitting || clientSubmitBlocked || (screen === "client_form" && parentClientsLoading)
              }
              loading={isSubmitting}
            >
              {isSubmitting ? t("modal_creating") : t("modal_create_button")}
            </ColocaButton>
          ))}
      </div>
    </div>
  );

  // Global progress stepper (mock: one stepper for the whole flow, recomputed per screen).
  //   internal + choice        → [Type(active), …(pending)]
  //   organization branch      → [Type, Company, Owner, Verify]        (success = Verify active)
  //   client branch            → [Type?, Identity, <type-dependent form steps>]
  const stepperSteps: ColocaStepperStep[] = [];
  let stepperCurrent = 0;
  if (isInternalUser && screen === "choice" && !createdOrganization) {
    stepperSteps.push({ label: t("wizard_step_type") }, { label: "…" });
  } else if (screen === "organization" || createdOrganization) {
    // Internal-only branch (org members cannot create organizations).
    stepperSteps.push(
      { label: t("wizard_step_type") },
      { label: t("wizard_step_company") },
      { label: t("wizard_step_owner") },
      { label: t("wizard_step_verify") },
    );
    stepperCurrent = createdOrganization ? 3 : 1 + organizationStep;
  } else {
    // The client-only shortcut has no entity choice, so it never gets the internal "Type" step.
    const hasTypeStep = isInternalUser && !clientOnly;
    const offset = hasTypeStep ? 1 : 0;
    if (hasTypeStep) stepperSteps.push({ label: t("wizard_step_type") });
    stepperSteps.push({ label: t("wizard_step_identity") });
    if (clientFormType === "business") {
      stepperSteps.push(
        { label: t("wizard_step_company") },
        { label: t("wizard_step_contact") },
        { label: t("wizard_step_owner") },
      );
    } else if (clientFormType === "individual") {
      stepperSteps.push({ label: t("wizard_step_details") });
    }
    if (offersAccountStep) {
      stepperSteps.push({ label: t("wizard_step_account") });
    } else {
      // The client branch always lands on the success/KYC screen: give that screen its own step so
      // success never highlights the last data-entry step.
      stepperSteps.push({ label: t("wizard_step_verify") });
    }
    stepperCurrent = createdClient
      ? stepperSteps.length - 1
      : screen === "client_type"
        ? offset
        : offset + 1 + (clientFormType === "business" ? businessStep : 0);
  }
  // Defensive clamp — keeps the active index valid even if a transient state combination shrinks the list.
  stepperCurrent = Math.min(stepperCurrent, stepperSteps.length - 1);

  const title = createdOrganization
    ? t("org_success_title")
    : createdClient
      ? t("client_success_title")
      : screen === "choice"
        ? t("modal_create_entity_title")
        : screen === "organization"
          ? t("modal_create_organization_title")
          : t("modal_create_title");

  // Mock drawer subtitles per screen (`titles[screen].s`).
  const description = createdOrganization
    ? createdOrgKycLink
      ? t("org_success_subtitle")
      : undefined
    : createdClient
      ? offersAccountStep
        ? t("client_success_subtitle_account")
        : t("client_success_subtitle")
      : screen === "choice"
        ? t("choice_entity_description")
        : screen === "organization"
          ? organizationStep === 0
            ? t("wizard_step_company")
            : t("wizard_step_owner")
          : screen === "client_type"
            ? t("drawer_client_description")
            : clientFormType === "business"
              ? businessStep === 2
                ? t("wizard_step_owner")
                : businessStep === 1
                  ? t("wizard_step_contact")
                  : t("wizard_step_company")
              : t("drawer_client_individual_description");

  return (
    <ColocaDrawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      size="lg"
      title={title}
      description={description}
      footer={footer}
    >
      <div className="space-y-4">
        <ColocaStepper
          steps={stepperSteps}
          currentStep={stepperCurrent}
          aria-label={t("wizard_progress_aria")}
          className="border-b border-border pb-4"
        />
        {createdOrganization && (
          <OrganizationSuccessStep organization={createdOrganization} kycLink={createdOrgKycLink} />
        )}
        {!createdOrganization && createdClient && (
          <ClientSuccessStep
            client={createdClient}
            kycLink={createdClientKycLink}
            accountCurrency={offersAccountStep ? successAccountCurrency : undefined}
            accountPrefill={offersAccountStep ? successAccountPrefill : undefined}
            onAccountCreated={handleAccountCreated}
            onSubmittingChange={setAccountSubmitting}
          />
        )}
        {!createdOrganization && !createdClient && screen === "choice" && (
          <CreateEntityChoice onSelect={handlePickEntity} />
        )}
        {!createdOrganization && !createdClient && screen === "client_type" && (
          <ClientTypeChoice value={clientFormType} onChange={setClientFormType} />
        )}

        {/* Branches stay mounted (hidden) so captured values survive Back navigation. */}
        {isInternalUser && (
          <div className={screen !== "organization" || createdOrganization ? "hidden" : undefined}>
            <OrganizationClientForm
              ref={organizationWizardRef}
              formId={ORGANIZATION_FORM_ID}
              onSubmit={handleSubmitOrganization}
              onStepChange={setOrganizationStep}
            />
          </div>
        )}

        <div
          className={
            screen !== "client_form" || createdOrganization || createdClient ? "hidden" : "space-y-4"
          }
        >
          {isInternalUser && (
            <>
              {parentClientError && (
                <div className="rounded-lg bg-warning-container p-3 text-sm text-warning-container-foreground">
                  {parentClientError}
                </div>
              )}
              <div className="grid gap-2">
                <ColocaLabel htmlFor={PARENT_ORGANIZATION_SELECT_ID}>
                  {t("modal_label_parent_organization")}
                </ColocaLabel>
                <ColocaCombobox
                  id={PARENT_ORGANIZATION_SELECT_ID}
                  value={parentClientId}
                  onValueChange={setParentClientId}
                  options={parentClientOptions}
                  placeholder={t("modal_placeholder_parent_organization")}
                  disabled={isSubmitting || parentClientsLoading}
                />
              </div>
            </>
          )}
          <div className={clientFormType === "business" ? "hidden" : undefined}>
            <PersonClientForm
              formId={PERSON_FORM_ID}
              onSubmit={handleCreateClient}
              organizationId={isInternalUser ? (parentClientId ?? undefined) : undefined}
            />
          </div>
          <div className={clientFormType !== "business" ? "hidden" : undefined}>
            <BusinessClientForm
              ref={businessWizardRef}
              formId={BUSINESS_FORM_ID}
              onSubmit={handleCreateClient}
              organizationId={isInternalUser ? (parentClientId ?? undefined) : undefined}
              onStepChange={setBusinessStep}
            />
          </div>
        </div>
      </div>
      {/* Sentinel: with a single body child, ColocaDrawer sticky-pins its last child (a helper meant for
          footer-less wizards). We render our actions in the `footer` prop, so force the normal flow. */}
      <span aria-hidden className="hidden" />
    </ColocaDrawer>
  );
}

export { CreateClientModal };
export default CreateClientModal;
